import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { AlertCircle, CheckCircle2, X, Upload } from 'lucide-react';
import { useCsvImport } from '@/hooks/useCsvImport';
import { type ParsedProspectData, type ImportError, type ImportState } from '@/pages/Import';
import { PIPELINE_STAGES } from '@/lib/constants';

interface ImportPreviewProps {
  data: ParsedProspectData[];
  errors: ImportError[];
  onImportComplete: (results: ImportState['results']) => void;
  onCancel: () => void;
}

export function ImportPreview({ data, errors, onImportComplete, onCancel }: ImportPreviewProps) {
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const { importProspects } = useCsvImport();

  const validData = data.filter((_, index) => {
    const rowNumber = index + 1;
    return !errors.some(error => error.row === rowNumber);
  });

  const getStageLabel = (stage: string) => {
    const stageInfo = PIPELINE_STAGES.find(s => s.value === stage);
    return stageInfo ? stageInfo.label : stage;
  };

  const getBadgeVariant = (hasError: boolean) => {
    return hasError ? 'destructive' : 'secondary';
  };

  const handleImport = async () => {
    if (validData.length === 0) {
      alert('No valid data to import');
      return;
    }

    setIsImporting(true);
    setImportProgress(0);

    try {
      // Simulate progress updates (in a real implementation, you might batch the imports)
      const progressInterval = setInterval(() => {
        setImportProgress(prev => Math.min(prev + 10, 90));
      }, 100);

      const results = await importProspects(validData);
      
      clearInterval(progressInterval);
      setImportProgress(100);

      // Wait a moment to show 100% completion
      setTimeout(() => {
        onImportComplete({
          successful: results.successful,
          failed: results.failed,
          errors: results.errors,
          createdProspects: results.createdProspects,
        });
      }, 500);

    } catch (error) {
      // Log error details in development only
      if (process.env.NODE_ENV === 'development') {
        console.error('Import failed:', error);
      }
      onImportComplete({
        successful: 0,
        failed: validData.length,
        errors: [{
          row: 0,
          message: error instanceof Error ? error.message : 'Import failed',
        }],
        createdProspects: [],
      });
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Summary Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-2">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              <div>
                <p className="text-2xl font-bold">{validData.length}</p>
                <p className="text-sm text-gray-500">Valid Prospects</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-2">
              <AlertCircle className="h-5 w-5 text-red-500" />
              <div>
                <p className="text-2xl font-bold">{errors.length}</p>
                <p className="text-sm text-gray-500">Errors Found</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-2">
              <Upload className="h-5 w-5 text-blue-500" />
              <div>
                <p className="text-2xl font-bold">{data.length}</p>
                <p className="text-sm text-gray-500">Total Rows</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Error Summary */}
      {errors.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <AlertCircle className="h-5 w-5 text-red-500" />
              <span>Import Errors</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {errors.map((error, index) => (
                <div key={index} className="p-3 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-sm font-medium text-red-800">
                    Row {error.row}: {error.message}
                  </p>
                  {error.field && (
                    <p className="text-xs text-red-600 mt-1">
                      Field: {error.field}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Data Preview */}
      <Card>
        <CardHeader>
          <CardTitle>Data Preview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="max-h-96 overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company</TableHead>
                  <TableHead>Contact Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Stage</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.slice(0, 50).map((prospect, index) => {
                  const rowNumber = index + 1;
                  const rowErrors = errors.filter(error => error.row === rowNumber);
                  const hasError = rowErrors.length > 0;

                  return (
                    <TableRow key={index} className={hasError ? 'bg-red-50' : ''}>
                      <TableCell>{prospect.company}</TableCell>
                      <TableCell>{prospect.contactName}</TableCell>
                      <TableCell>{prospect.email}</TableCell>
                      <TableCell>{prospect.phone || '-'}</TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {getStageLabel(prospect.stage || 'new')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={getBadgeVariant(hasError)}>
                          {hasError ? 'Error' : 'Valid'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {data.length > 50 && (
              <p className="text-sm text-gray-500 mt-4 text-center">
                Showing first 50 rows of {data.length} total rows
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Import Progress */}
      {isImporting && (
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Importing prospects...</span>
                <span>{importProgress}%</span>
              </div>
              <Progress value={importProgress} className="h-2" />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Action Buttons */}
      <div className="flex justify-between">
        <Button variant="outline" onClick={onCancel} disabled={isImporting}>
          <X className="h-4 w-4 mr-2" />
          Cancel
        </Button>
        
        <Button 
          onClick={handleImport} 
          disabled={isImporting || validData.length === 0}
        >
          <Upload className="h-4 w-4 mr-2" />
          {isImporting ? 'Importing...' : `Import ${validData.length} Prospects`}
        </Button>
      </div>
    </div>
  );
}